/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

import isNil from 'lodash/isNil';

/**
 * Komunikátor médiaplánů přehrávaných stop playlistu pro Ki-Wi Server.
 */
class TrackPlanCommunicator {
  /**
   * @param {DhtmlxToIcalExporter} dhtmlxToIcalExporter
   */
  constructor(dhtmlxToIcalExporter) {
    this.dhtmlxToIcalExporter = dhtmlxToIcalExporter;
  }

  /**
   * Vygeneruje pseudo-unikátní identifikátor iCalendar události.
   * Identifikátor je unikátní v rámci Player aplikace,
   * což by mělo požadavkům specifikace vyhovět.
   * @param {DhtmlxEvent} dhtmlxEvent
   * @private
   * @returns {string}
   */
  generateUid(dhtmlxEvent) {
    return `TrackPlan-${dhtmlxEvent.id}`;
  }

  /**
   * Načte DHTMLX Scheduler událost z formátu médiaplánů přehrávaných stop.
   * @param {Object} trackPlan Část odpovědi na obsah zařízení s jednou událostí
   * médiaplánu stopy.
   * @returns {DhtmlxEvent}
   */
  parseDhtmlxEvent(trackPlan) {
    return {
      end_date: trackPlan.endDate,
      event_length: this.parseString(trackPlan.event_length),
      event_pid: this.parseString(trackPlan.event_pid),
      id: String(trackPlan.id),
      rec_type: trackPlan.rec_type,
      start_date: trackPlan.startDate,
      // Popis události se zahodí, není potřeba.
      text: ''
    };
  }

  /**
   * Načte ze stopy kalendář s médiaplánem, pokud stapa nějaký obsahuje.
   * @param {Array.<Object>} trackPlans Část odpovědi na obsah zařízení
   * se seznamem událostí médiaplánu stopy.
   * Používá se mírně upravený DHTMLX Scheduler formát událostí.
   * @returns {null|string}
   */
  parseIcal(trackPlans) {
    // Převod položek `TrackPlan` na `DhtmlxEvent`
    const dhtmlxEvents = trackPlans.map((trackPlan) =>
      this.parseDhtmlxEvent(trackPlan)
    );
    // Převod seznamu položek `DhtmlxEvent` na řetězec ve formátu na iCalendar.
    const ical = this.dhtmlxToIcalExporter.export(dhtmlxEvents, {
      generateUid: (...args) => this.generateUid(...args)
    });
    // Pokud je výsledek `null` (iCalendar nepodporuje kalendář bez událostí),
    // tak to znamená, že zde nejsou žádné validní iCalendar události.
    // To znamená že zde nebyly ani žádné DHTMLX události, nebo tyto události
    // nevytvořili žádnou hlavní iCalendar událost.
    // Druhá varianta by se mohla stát pouze teoreticky při poškozeném vstupu.
    if (isNil(ical)) {
      return null;
    }
    // Podařilo se nějaký validní iCalendar vytvořit.
    return ical;
  }

  /**
   * Načte zpět řetězec z hodnot, které server převádí na čísla.
   * Pro vyjádření prázdného řetězce se používá číslo -1.
   * @param {number} number
   * @private
   * @returns {string}
   */
  parseString(number) {
    if (number === -1) {
      return '';
    }
    return String(number);
  }
}

export default TrackPlanCommunicator;
